/**
 * French Canadian Currency Conversion
 * Converts USD format ($XX.XX) to French Canadian format (XX,XX $)
 * Uses MutationObserver to handle dynamically loaded content
 */

const convertCurrency = (/** @type {string} */ text) => {
  const caFrenchFormat = new Intl.NumberFormat('fr-CA', {
    style: 'currency',
    currency: 'CAD',
    currencyDisplay: 'narrowSymbol'
  });

  const regex = /\$\s?(\d{1,3}(?:,\d{3})*(?:\.\d{2})?|\d+(?:\.\d{2})?)/g;

  return text.replace(regex, (/** @type {string} */ match, /** @type {string} */ amount) => {
    const cleanAmount = parseFloat(amount.replace(/,/g, ''));
    return caFrenchFormat.format(cleanAmount);
  });
};

// Use a WeakSet to track nodes we've already modified 
// to prevent infinite loops when the observer detects our own changes.
const processedNodes = new WeakSet();

/**
 * Process all text nodes within a given root element, including shadow roots
 * @param {Node} root - The root element to search for text nodes
 */
const processTextNodes = (root) => {
  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) => {
        // Skip script and style tags
        const parent = node.parentElement;
        if (parent && (parent.tagName === 'SCRIPT' || parent.tagName === 'STYLE')) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    }
  );

  let textNode;
  while (textNode = walker.nextNode()) {
    const original = textNode.nodeValue;
    if (!original) continue;
    
    const converted = convertCurrency(original);
    
    // Only skip if already processed AND content doesn't need conversion
    if (processedNodes.has(textNode) && original === converted) {
      continue;
    }
    
    if (original !== converted) {
      processedNodes.add(textNode);
      textNode.nodeValue = converted;
    }
  }
};

const convertInitialContent = () => {
  processTextNodes(document.body);
};

/**
 * Process Recharge subscription widget shadow DOM
 */
const processRechargeWidgets = () => {
  const rechargeWidgets = document.querySelectorAll('recharge-subscription-widget');
  rechargeWidgets.forEach((/** @type {Element} */ widget) => {
    if (widget.shadowRoot) {
      processTextNodes(widget.shadowRoot);
    }
  });
};

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    // Check for newly added nodes or text content changes
    const nodes = [...mutation.addedNodes];
    if (mutation.type === 'characterData') nodes.push(mutation.target);

    nodes.forEach(node => {
      // Process text nodes directly
      if (node.nodeType === Node.TEXT_NODE) {
        const original = node.nodeValue;
        if (!original) return;
        
        const converted = convertCurrency(original);
        
        // Only skip if already processed AND content doesn't need conversion
        if (processedNodes.has(node) && original === converted) return;
        
        if (original !== converted) {
          processedNodes.add(node); // Mark as processed
          node.nodeValue = converted;
        }
      } 
      // If it's an element, process all text nodes inside it
      else if (node.nodeType === Node.ELEMENT_NODE) {
        processTextNodes(node);
      }
    });
  }
  
  // Special handling for Recharge subscription widget shadow DOM
  processRechargeWidgets();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    convertInitialContent();
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });
  });
} else {
  convertInitialContent();
  
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true
  });
}
